import type {
  BulkGetSessionLocationsOutput,
  BulkSessionDataInput,
  Category,
  CategoryOutput,
  CreateCategoryInput,
  CreateSessionInput,
  // Session location types
  CreateSessionLocationInput,
  CreateTagInput,
  DeleteSessionLocationOutput,
  GetSessionsByTagsOrCategoriesInput,
  JoinSessionInput,
  SessionAttendeeRole,
  SessionCategoriesOutput,
  SessionFavoriteCountOutput,
  SessionLocation,
  SessionOutput,
  SessionTagsOutput,
  SessionVoteCountOutput,
  Tag,
  TagOutput,
  UpdateCategoryInput,
  UpdateSessionInput,
  UpdateSessionLocationInput,
  UpdateTagInput,
} from '@sunrise-ui/api/events/api';
import { SessionStatus, TemporalRelation } from '@sunrise-ui/api/events/api';
import {
  useAuthStore,
  useCacheAvailability,
  useSnackbar,
} from '@sunrise-ui/primitives';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { QueryKeys } from '../constants/QueryKeys';
import {
  gramsApi,
  sessionsApi,
  useAuthMutation,
  useAuthQuery,
} from '../utils/api';
import { assertParameterIsNotUndefinedOrNull } from '../utils/error';
import { SessionRole } from '../utils/sessions';

const {
  sessionsKey,
  byEventIdKey,
  tagsKey,
  votesKey,
  voteCountKey,
  attendeesKey,
  byTagsKey,
  categoriesKey,
  bulkSessionDataKey,
  bulkVotesKey,
  userSessionVotesKey,
  tagCategoriesDetailsKey,
  viewedKey,
  analyticsKey,
  locationsKey,
  favoritesKey,
  favoriteCountKey,
  bulkFavoritesKey,
  userSessionFavoritesKey,
  temporalKey,
  allUserFavoritesKey,
  canonical,
} = QueryKeys.sessions;

// Grams query keys used for invalidation after social share creation
const { socialSharesKey } = QueryKeys.grams;

export const useGetSessionsByEventId = (
  eventId?: string,
  shuffled?: boolean,
  communityId?: string,
) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(eventId, 'id');
      const sessions = await sessionsApi
        .getSessionsByEventSessionsEventsEventIdGet({
          eventId,
          isShuffled: shuffled,
        })
        .then(({ data }) => data.sessions);

      // Cache each session
      for (const session of sessions) {
        queryClient.setQueryData([sessionsKey, session.id], session);
      }

      return sessions;
    },
    queryKey: [
      sessionsKey,
      byEventIdKey,
      eventId,
      communityId,
      Boolean(shuffled),
    ],
    enabled: Boolean(eventId),
  });
};

export const useGetTemporalPublicSessions = ({
  temporalRelation,
  communityIds,
  filterStatus,
  enabled,
  startDateFrom,
  startDateTo,
}: {
  temporalRelation: TemporalRelation;
  communityIds?: string[];
  filterStatus?: SessionStatus;
  enabled?: boolean;
  startDateFrom?: string;
  startDateTo?: string;
}) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryFn: async () => {
      const sessions = await sessionsApi
        .publicBulkGetSessionsTemporalSessionsTemporalPublicPost({
          bulkGetSessionsTemporalInput: {
            temporal_relation: temporalRelation,
            community_ids: communityIds,
            start_date_from: startDateFrom,
            start_date_to: startDateTo,
          },
        })
        .then((response) => response.data.sessions);

      // Cache each session
      for (const session of sessions) {
        queryClient.setQueryData([sessionsKey, session.id], session);
      }

      return sessions;
    },
    enabled,
    queryKey: [
      sessionsKey,
      temporalKey,
      temporalRelation,
      communityIds,
      startDateFrom,
      startDateTo,
    ].filter(Boolean),
    select: (data) =>
      data?.filter((session) => {
        if (filterStatus) return session.status === filterStatus;
        return true;
      }),
  });
};

// Hook for leaving a session (removing an attendee from a session)
export const useLeaveSession = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async ({
      sessionId,
      userId,
    }: {
      sessionId: string;
      userId?: string;
      eventId?: string;
    }) => {
      return sessionsApi.leaveSessionSessionsSessionIdLeavePost({
        sessionId,
        userId,
      });
    },
    onSuccess: (_, { sessionId, eventId }) => {
      // Invalidate session attendees to refresh the list
      void queryClient.invalidateQueries({
        queryKey: [sessionsKey, attendeesKey, sessionId],
      });

      // Invalidate bulk session data that might contain attendee information
      void queryClient.invalidateQueries({
        queryKey: [sessionsKey, bulkSessionDataKey],
      });

      // Also refresh event sessions list/cards if eventId provided
      if (eventId) {
        void queryClient.invalidateQueries({
          queryKey: [sessionsKey, byEventIdKey, eventId],
        });
      }
    },
    onError: (error) => {
      console.error('Error leaving session:', error);
      showSnackbar({
        text: 'Failed to remove attendee from session',
        type: 'error',
      });
    },
  });
};

// Hook for updating an attendee's role for a session within a community
export const useUpdateAttendeeRole = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async ({
      attendeeId,
      role,
      communityId,
    }: {
      sessionId: string;
      attendeeId: string;
      role: SessionAttendeeRole;
      communityId: string;
      eventId?: string;
    }) => {
      return sessionsApi.updateAttendeeRoleSessionsAttendeesRoleCommunitiesCommunityIdPut(
        {
          communityId,
          updateSessionAttendeeRoleInput: {
            attendee_id: attendeeId,
            role,
          },
        },
      );
    },
    onSuccess: (_, { sessionId, eventId }) => {
      // Invalidate session attendees to refresh the list
      void queryClient.invalidateQueries({
        queryKey: [sessionsKey, attendeesKey, sessionId],
      });

      // Invalidate bulk session data that might contain attendee information
      void queryClient.invalidateQueries({
        queryKey: [sessionsKey, bulkSessionDataKey],
      });

      // Also refresh event sessions list/cards if eventId provided
      if (eventId) {
        void queryClient.invalidateQueries({
          queryKey: [sessionsKey, byEventIdKey, eventId],
        });
      }
    },
    onError: (error) => {
      console.error('Error updating attendee role:', error);
      showSnackbar({
        text: 'Failed to update attendee role',
        type: 'error',
      });
    },
  });
};

export const useGetSessionsByTags = (tagIds: string[]) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryFn: async () => {
      const sessions = await sessionsApi
        .getSessionsByTagsSessionsSearchByTagsPost({
          getSessionsByTagsInput: {
            tag_ids: tagIds,
            match_all: false,
          },
        })
        .then(({ data }) => data.sessions);

      for (const session of sessions) {
        queryClient.setQueryData([sessionsKey, session.id], session);
      }

      return sessions;
    },
    queryKey: [sessionsKey, byTagsKey, tagIds.join(',')],
    enabled: tagIds.length > 0,
  });
};

export const useGetSession = (sessionId?: string) => {
  const { isQueryKeyAvailable } = useCacheAvailability();

  return useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(sessionId, 'sessionId');

      return sessionsApi
        .getSessionSessionsSessionIdGet({
          sessionId,
        })
        .then(({ data }) => data);
    },
    queryKey: [sessionsKey, sessionId],
    enabled: Boolean(sessionId) && !isQueryKeyAvailable([sessionsKey]),
  });
};

// Custom hook to get bulk session vote counts for multiple sessions

export const useGetBulkSessionVoteCounts = ({
  sessionIds,
  eventId,
  enabled = true,
}: {
  sessionIds: string[];
  eventId: string;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [sessionsKey, bulkVotesKey, eventId],
    queryFn: async () => {
      if (sessionIds.length === 0) {
        return { counts: [] as SessionVoteCountOutput[] };
      }

      // Call the bulk endpoint
      const response = await sessionsApi
        .bulkGetSessionVoteCountsSessionsVotesCountBulkPost({
          bulkSessionIdsInput: {
            session_ids: sessionIds,
          },
        })
        .then(({ data }) => data);

      // Cache individual vote counts for downstream hooks
      if (response.counts && Array.isArray(response.counts)) {
        response.counts.forEach((voteCount) => {
          if (voteCount.session_id) {
            // Cache each session's vote count for the useGetSessionVoteCount hook
            // This ensures the SessionVoteButton can display the vote count without additional API calls
            queryClient.setQueryData(
              [sessionsKey, votesKey, voteCountKey, voteCount.session_id],
              voteCount,
            );
          }
        });
      }

      return response;
    },
    enabled: enabled && sessionIds.length > 0,
  });
};

export const useGetSessionTags = (sessionId?: string) => {
  const { isQueryKeyAvailable } = useCacheAvailability();

  return useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(sessionId, 'sessionId');
      return sessionsApi
        .getSessionTagsSessionsSessionIdTagsGet({
          sessionId,
        })
        .then(({ data }) => data.session_tags);
    },
    queryKey: [sessionsKey, tagsKey, sessionId],
    enabled: Boolean(sessionId) && !isQueryKeyAvailable([sessionsKey, tagsKey]),
  });
};

export const useGetSessionCategories = (sessionId?: string) => {
  const { isQueryKeyAvailable } = useCacheAvailability();

  return useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(sessionId, 'sessionId');
      return sessionsApi
        .getSessionCategoriesSessionsSessionIdCategoriesGet({
          sessionId,
        })
        .then(({ data }) => data.session_categories);
    },
    queryKey: [sessionsKey, categoriesKey, sessionId],
    enabled:
      Boolean(sessionId) && !isQueryKeyAvailable([sessionsKey, categoriesKey]),
  });
};

// Hook for fetching individual tag details by ID
export const useGetTag = (tagId?: string) =>
  useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(tagId, 'tagId');
      return sessionsApi
        .getTagSessionsTagsTagIdGet({
          tagId,
        })
        .then(({ data }) => data.tag);
    },
    queryKey: [tagsKey, tagId],
    enabled: Boolean(tagId),
  });

// Hook for fetching individual category details by ID
export const useGetCategory = (categoryId?: string) =>
  useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(categoryId, 'categoryId');
      return sessionsApi
        .getCategorySessionsCategoriesCategoryIdGet({
          categoryId,
        })
        .then(({ data }) => data.category);
    },
    queryKey: [categoriesKey, categoryId],
    enabled: Boolean(categoryId),
  });

export const useGetSessionVotes = (sessionId?: string) =>
  useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(sessionId, 'sessionId');

      return sessionsApi
        .getSessionVotesSessionsSessionIdVotesGet({
          sessionId,
        })
        .then(({ data }) => data.votes);
    },
    queryKey: [sessionsKey, votesKey, sessionId],
    enabled: Boolean(sessionId),
  });

// Hook for fetching all sessions that a specific user has voted on within an event
export const useGetUserSessionVotes = (
  eventId?: string,
  userId?: string,
  communityId?: string,
) => {
  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(eventId, 'eventId');
      assertParameterIsNotUndefinedOrNull(userId, 'userId');
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');

      return sessionsApi
        .getUserSessionVotesSessionsVotesEventsEventIdUsersUserIdCommunitiesCommunityIdGet(
          {
            eventId: eventId!,
            userId: userId!,
            communityId: communityId as string,
          },
        )
        .then(({ data }) => data.sessions);
    },
    queryKey: [sessionsKey, userSessionVotesKey, eventId, userId, communityId],
    enabled: Boolean(eventId) && Boolean(userId),
  });
};

export const useGetSessionAttendees = (
  sessionId?: string,
  onlySpeakers?: boolean,
) => {
  const { isQueryKeyAvailable } = useCacheAvailability();

  return useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(sessionId, 'sessionId');
      return sessionsApi
        .getSessionAttendeesSessionsSessionIdAttendeesGet({
          sessionId,
        })
        .then(({ data }) => data.attendees);
    },
    select: (data) => {
      if (onlySpeakers)
        return data.filter(
          (a) =>
            a.role === SessionRole.SPEAKER || a.role === SessionRole.MODERATOR,
        );
      else return data;
    },
    queryKey: [sessionsKey, attendeesKey, sessionId],
    enabled:
      Boolean(sessionId) && !isQueryKeyAvailable([sessionsKey, attendeesKey]),
  });
};

export const useVoteForSession = (sessionId?: string) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async () => {
      if (!sessionId) return;
      // Get currentUserId at execution time to avoid stale closure issues
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return sessionsApi
        .voteOnSessionSessionsSessionIdVotePost({
          sessionVoteInput: {
            user_id: currentUserId,
          },
          sessionId,
        })
        .then(({ data }) => data);
    },
    async onSuccess() {
      // Update individual session vote count (increment by 1)
      const sessionVoteCountQueryKey = [
        sessionsKey,
        votesKey,
        voteCountKey,
        sessionId,
      ];
      const currentVoteCount = queryClient.getQueryData<{
        vote_count?: number;
      }>(sessionVoteCountQueryKey);
      if (currentVoteCount) {
        queryClient.setQueryData(sessionVoteCountQueryKey, {
          ...currentVoteCount,
          vote_count: (currentVoteCount.vote_count || 0) + 1,
        });
      }

      // Update bulk session vote counts cache
      // Find all bulk session vote counts queries
      const bulkQueriesData = queryClient.getQueriesData<{
        counts: Array<{ session_id: string; vote_count: number }>;
      }>({ queryKey: [sessionsKey, bulkVotesKey] });

      // Update the vote count in each bulk query that contains this session
      bulkQueriesData.forEach(([queryKey, data]) => {
        if (data?.counts) {
          const updatedCounts = data.counts.map((count) => {
            if (count.session_id === sessionId) {
              return { ...count, vote_count: (count.vote_count || 0) + 1 };
            }
            return count;
          });

          // Set the updated bulk data in the cache
          queryClient.setQueryData(queryKey, { counts: updatedCounts });
        }
      });

      // Invalidate the user session votes queries
      // Note that this invalidates for all users for all events, but there should only be the current user
      // and it's unlikely that they will vote on multiple events at the same time
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, userSessionVotesKey],
      });
    },
    async onError(error: unknown) {
      // Check if this is a 400 error indicating user has already voted
      if (isAxiosError(error) && error.response?.status === 400) {
        showSnackbar({
          type: 'error',
          text: "You've already voted!",
        });
      } else {
        showSnackbar({
          type: 'error',
          text: 'Failed to vote. Please try again',
        });
      }
    },
  });
};

export const useRemoveVoteForSession = (sessionId?: string) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async () => {
      if (!sessionId) return;
      // Get currentUserId at execution time to avoid stale closure issues
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return sessionsApi
        .removeSessionVoteSessionsSessionIdVoteUserIdDelete({
          userId: currentUserId,
          sessionId,
        })
        .then((response) => response.data);
    },
    async onSuccess() {
      // Update individual session vote count (decrement by 1)
      const sessionVoteCountQueryKey = [
        sessionsKey,
        votesKey,
        voteCountKey,
        sessionId,
      ];
      const currentVoteCount = queryClient.getQueryData<{
        vote_count?: number;
      }>(sessionVoteCountQueryKey);
      if (currentVoteCount) {
        const newCount = Math.max(0, (currentVoteCount.vote_count || 0) - 1);
        queryClient.setQueryData(sessionVoteCountQueryKey, {
          ...currentVoteCount,
          vote_count: newCount,
        });
      }

      // Update bulk session vote counts cache
      // Find all bulk session vote counts queries
      const bulkQueriesData = queryClient.getQueriesData<{
        counts: Array<{ session_id: string; vote_count: number }>;
      }>({ queryKey: [sessionsKey, bulkVotesKey] });

      // Update the vote count in each bulk query that contains this session
      bulkQueriesData.forEach(([queryKey, data]) => {
        if (data?.counts) {
          const updatedCounts = data.counts.map((count) => {
            if (count.session_id === sessionId) {
              // Ensure count doesn't go below 0
              const newCount = Math.max(0, (count.vote_count || 0) - 1);
              return { ...count, vote_count: newCount };
            }
            return count;
          });

          // Set the updated bulk data in the cache
          queryClient.setQueryData(queryKey, { counts: updatedCounts });
        }
      });

      // Invalidate the user session votes queries
      // Note that this invalidates for all users for all events, but there should only be the current user
      // and it's unlikely that they will vote on multiple events at the same time
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, userSessionVotesKey],
      });
    },
    async onError() {
      showSnackbar({
        type: 'error',
        text: 'Failed to remove vote. Please try again',
      });
    },
  });
};

export const useGetBulkSessionData = ({
  sessionIds,
  attendeeRole,
  enabled = true,
}: {
  sessionIds: string[];
  attendeeRole?: SessionAttendeeRole;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: attendeeRole
      ? [sessionsKey, bulkSessionDataKey, attendeeRole, sessionIds]
      : [sessionsKey, bulkSessionDataKey, sessionIds],
    queryFn: async () => {
      const input: BulkSessionDataInput = {
        session_ids: sessionIds,
      };

      if (attendeeRole) {
        input.attendee_role = attendeeRole;
      }

      const response = await sessionsApi.bulkGetSessionDataSessionsBulkDataPost(
        {
          bulkSessionDataInput: input,
        },
      );

      const bulkData = response.data;

      // Cache data for individual hooks to use (similar to the old voting page hook)
      if (bulkData.session_data) {
        for (const sessionData of bulkData.session_data) {
          const { session_id, data } = sessionData;

          // Individual session data is already cached by useGetSessionsByEventId
          // on the voting page, so no need to cache it here

          // Cache session tags for useGetSessionTags
          if (data.session_tags) {
            queryClient.setQueryData(
              [sessionsKey, tagsKey, session_id],
              data.session_tags,
            );
          }

          // Cache session categories for useGetSessionCategories
          if (data.session_categories) {
            queryClient.setQueryData(
              [sessionsKey, categoriesKey, session_id],
              data.session_categories,
            );
          }

          // Cache session attendees for useGetSessionAttendees
          if (data.attendees) {
            queryClient.setQueryData(
              [sessionsKey, attendeesKey, session_id],
              data.attendees,
            );
          }

          // Note: Votes are not part of the bulk data response yet
          // If they are added in the future, they can be cached here
        }
      }

      return bulkData;
    },
    enabled: Boolean(enabled && sessionIds.length > 0),
  });
};

// Hook for fetching all sessions that a user has viewed within an event
export const useGetUserViewedSessions = (eventId?: string, userId?: string) => {
  return useAuthQuery({
    queryKey: [sessionsKey, viewedKey, eventId, userId],
    queryFn: async () => {
      // Return empty array if parameters are undefined to prevent query errors
      if (!eventId || !userId) {
        return [];
      }

      return sessionsApi
        .getSessionsViewedByUserInEventSessionsUsersUserIdEventsEventIdSessionsViewedGet(
          {
            userId,
            eventId,
          },
        )
        .then(
          (response) =>
            (
              response.data as unknown as {
                sessions: Array<{ id: string }>;
              }
            ).sessions || [],
        );
    },
    enabled: Boolean(eventId && userId),
  });
};

// Combined hook for fetching both session categories and tags for display using bulk data
// This hook now exclusively uses bulk data and does not make individual API calls
export const useGetSessionTagsAndCategories = (
  sessionId?: string,
  eventId?: string,
) => {
  const queryClient = useQueryClient();

  // Get bulk session data from cache
  const bulkSessionDataQueryKey = [sessionsKey, bulkSessionDataKey, eventId];
  const cachedBulkData = queryClient.getQueryData(bulkSessionDataQueryKey);

  return useQuery({
    queryKey: [sessionsKey, tagCategoriesDetailsKey, sessionId, eventId],
    queryFn: async () => {
      if (!sessionId) {
        return { tags: [], categories: [] };
      }

      // Get session tags and categories from bulk data cache
      let sessionTags: Array<{ tag_id: string }> = [];
      let sessionCategories: Array<{ category_id: string }> = [];

      // Check if we have bulk data in cache
      if (cachedBulkData) {
        // Find this session's data in the bulk cache
        const sessionData = (
          cachedBulkData as {
            session_data?: Array<{
              session_id: string;
              data?: {
                session_tags?: Array<{ tag_id: string }>;
                session_categories?: Array<{ category_id: string }>;
              };
            }>;
          }
        ).session_data?.find((data) => data.session_id === sessionId);

        if (sessionData?.data) {
          sessionTags = sessionData.data.session_tags || [];
          sessionCategories = sessionData.data.session_categories || [];
        }
      }

      // Extract the canonical tag/category IDs
      const tagIds =
        sessionTags?.map(({ tag_id }) => tag_id).filter(Boolean) || [];
      const categoryIds =
        sessionCategories
          ?.map(({ category_id }) => category_id)
          .filter(Boolean) || [];

      // Look up canonical tag data from cache (no fallback to API calls)
      const tags: Tag[] = tagIds
        .map((tagId) => {
          const cachedTag = queryClient.getQueryData([tagsKey, tagId]);
          return cachedTag as Tag | undefined;
        })
        .filter((tag): tag is Tag => Boolean(tag));

      // Look up canonical category data from cache (no fallback to API calls)
      const categories: Category[] = categoryIds
        .map((categoryId) => {
          const cachedCategory = queryClient.getQueryData([
            categoriesKey,
            categoryId,
          ]);
          return cachedCategory as Category | undefined;
        })
        .filter((category): category is Category => Boolean(category));

      return { tags, categories };
    },
    enabled: Boolean(sessionId),
  });
};

// Creation hooks
export const useCreateSession = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    SessionOutput,
    unknown,
    { communityId?: string; createSessionInput: CreateSessionInput }
  >({
    mutationFn: async ({ communityId, createSessionInput }) => {
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');

      const response =
        await sessionsApi.createSessionSessionsCommunitiesCommunityIdPost({
          communityId: communityId as string,
          createSessionInput,
        });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [sessionsKey] });
      showSnackbar({
        text: 'Session created successfully!',
        type: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        text: 'Failed to create session',
        type: 'error',
      });
    },
  });
};

export const useUpdateTag = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation<
    TagOutput,
    unknown,
    { tagId: string; communityId: string; updateTagInput: UpdateTagInput }
  >({
    mutationFn: async ({ tagId, communityId, updateTagInput }) => {
      assertParameterIsNotUndefinedOrNull(tagId, 'tagId');
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      const response =
        await sessionsApi.updateTagSessionsTagsTagIdCommunitiesCommunityIdPut({
          tagId,
          communityId,
          updateTagInput,
        });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: [sessionsKey, tagsKey] });
      showSnackbar({
        text: 'Tag updated successfully!',
        type: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        text: 'Failed to update tag',
        type: 'error',
      });
    },
  });
};

export const useCreateTag = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation<
    TagOutput,
    unknown,
    { communityId?: string; createTagInput: CreateTagInput }
  >({
    mutationFn: async ({ communityId, createTagInput }) => {
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      const response =
        await sessionsApi.createTagSessionsTagsCommunitiesCommunityIdPost({
          communityId: communityId as string,
          createTagInput,
        });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [sessionsKey, tagsKey] });
      showSnackbar({
        text: 'Tag created successfully!',
        type: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        text: 'Failed to create tag',
        type: 'error',
      });
    },
  });
};

export const useUpdateCategory = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation<
    CategoryOutput,
    unknown,
    {
      categoryId: string;
      communityId: string;
      updateCategoryInput: UpdateCategoryInput;
    }
  >({
    mutationFn: async ({ categoryId, communityId, updateCategoryInput }) => {
      assertParameterIsNotUndefinedOrNull(categoryId, 'categoryId');
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      const response =
        await sessionsApi.updateCategorySessionsCategoriesCategoryIdCommunitiesCommunityIdPut(
          {
            categoryId,
            communityId,
            updateCategoryInput,
          },
        );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: [sessionsKey, categoriesKey] });
      showSnackbar({
        text: 'Category updated successfully!',
        type: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        text: 'Failed to update category',
        type: 'error',
      });
    },
  });
};

export const useCreateCategory = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation<
    CategoryOutput,
    unknown,
    { communityId?: string; createCategoryInput: CreateCategoryInput }
  >({
    mutationFn: async ({ communityId, createCategoryInput }) => {
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      const response =
        await sessionsApi.createCategorySessionsCategoriesCommunitiesCommunityIdPost(
          {
            communityId: communityId as string,
            createCategoryInput,
          },
        );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [sessionsKey, categoriesKey] });
      showSnackbar({
        text: 'Category created successfully!',
        type: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        text: 'Failed to create category',
        type: 'error',
      });
    },
  });
};

// Hook for fetching all canonical tags by event ID (for caching optimization)
export const useGetCanonicalTagsByEvent = (eventId?: string) => {
  const { isQueryKeyAvailable } = useCacheAvailability();

  return useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(eventId, 'eventId');

      return sessionsApi
        .getTagsByEventSessionsEventsEventIdTagsGet({
          eventId,
        })
        .then(({ data }) => data.tags);
    },
    queryKey: [sessionsKey, tagsKey, canonical, eventId],
    enabled:
      Boolean(eventId) &&
      !isQueryKeyAvailable([sessionsKey, tagsKey, canonical]),
  });
};

// Hook for fetching all canonical categories by event ID (for caching optimization)
export const useGetCanonicalCategoriesByEvent = (eventId?: string) => {
  const { isQueryKeyAvailable } = useCacheAvailability();

  return useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(eventId, 'eventId');

      return sessionsApi
        .getCategoriesByEventSessionsEventsEventIdCategoriesGet({
          eventId,
        })
        .then(({ data }) => data.categories);
    },
    queryKey: [sessionsKey, categoriesKey, canonical, eventId],
    enabled:
      Boolean(eventId) &&
      !isQueryKeyAvailable([sessionsKey, categoriesKey, canonical]),
  });
};

// Bulk Canonical Session Tags: Get canonical session tags for multiple event IDs
export const useBulkGetCanonicalTagsByEvents = (eventIds?: string[]) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [sessionsKey, tagsKey, canonical, 'bulk', eventIds],
    enabled: Boolean(eventIds?.length),
    queryFn: async (): Promise<Tag[]> => {
      assertParameterIsNotUndefinedOrNull(eventIds, 'eventIds');

      const data = await sessionsApi
        .getTagsByEventsSessionsEventsBulkTagsPost({
          bulkGetTagsByEventsInput: {
            event_ids: eventIds,
          },
        })
        .then((response) => response.data);

      for (const canonicalTag of data.tags) {
        queryClient.setQueryData<Array<Tag>>(
          [sessionsKey, tagsKey, canonical, canonicalTag.event_id],
          (oldTags) => {
            const tags = oldTags || [];
            tags?.push(canonicalTag);
            return tags;
          },
        );
      }

      return data.tags;
    },
    staleTime: 300000,
  });
};

// Bulk Canonical Session Categories: Get canonical session categories for multiple event IDs
export const useBulkGetCanonicalCategoriesByEvents = (eventIds?: string[]) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [sessionsKey, categoriesKey, canonical, 'bulk', eventIds],
    enabled: Boolean(eventIds?.length),
    queryFn: async (): Promise<Category[]> => {
      assertParameterIsNotUndefinedOrNull(eventIds, 'eventIds');

      const data = await sessionsApi
        .getCategoriesByEventsSessionsEventsBulkCategoriesPost({
          bulkGetCategoriesByEventsInput: {
            event_ids: eventIds,
          },
        })
        .then((response) => response.data);

      for (const canonicalCategory of data.categories) {
        queryClient.setQueryData<Array<Category>>(
          [sessionsKey, categoriesKey, canonical, canonicalCategory.event_id],
          (oldCategories) => {
            const categories = oldCategories || [];
            categories?.push(canonicalCategory);
            return categories;
          },
        );
      }

      return data.categories;
    },
    staleTime: 300000,
  });
};

// Cache-aware Canonical Session Tags: Use bulk cache if available, otherwise fall back to individual call
export const useGetTagsByEventWithBulkFallback = (eventId?: string) => {
  const { isQueryKeyAvailable } = useCacheAvailability();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [sessionsKey, tagsKey, eventId],
    enabled:
      Boolean(eventId) && !isQueryKeyAvailable([sessionsKey, tagsKey, 'bulk']),
    queryFn: async (): Promise<Tag[]> => {
      if (!eventId) return [];

      // First try to get from bulk cache
      const queryCache = queryClient.getQueryCache();
      let cachedTags: Tag[] = [];

      queryCache.getAll().forEach((query) => {
        const { queryKey } = query;
        if (
          Array.isArray(queryKey) &&
          queryKey[0] === sessionsKey &&
          queryKey[1] === tagsKey &&
          queryKey[2] === 'bulk' &&
          query.state.data
        ) {
          const bulkData = query.state.data as Record<string, Tag[]>;
          if (bulkData[eventId]) {
            cachedTags = bulkData[eventId];
          }
        }
      });

      // If we found bulk cached data, return it
      if (cachedTags.length > 0) {
        return cachedTags;
      }

      // Otherwise, make individual API call
      assertParameterIsNotUndefinedOrNull(eventId, 'eventId');
      const tags = await sessionsApi
        .getTagsByEventSessionsEventsEventIdTagsGet({
          eventId,
        })
        .then(({ data }) => data.tags || []);

      return tags;
    },
    staleTime: 300000,
  });
};

// Cache-aware Canonical Session Categories: Use bulk cache if available, otherwise fall back to individual call
export const useGetCategoriesByEventWithBulkFallback = (eventId?: string) => {
  const { isQueryKeyAvailable } = useCacheAvailability();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [sessionsKey, categoriesKey, eventId],
    enabled:
      Boolean(eventId) &&
      !isQueryKeyAvailable([sessionsKey, categoriesKey, 'bulk']),
    queryFn: async (): Promise<Category[]> => {
      if (!eventId) return [];

      // First try to get from bulk cache
      const queryCache = queryClient.getQueryCache();
      let cachedCategories: Category[] = [];

      queryCache.getAll().forEach((query) => {
        const { queryKey } = query;
        if (
          Array.isArray(queryKey) &&
          queryKey[0] === sessionsKey &&
          queryKey[1] === categoriesKey &&
          queryKey[2] === 'bulk' &&
          query.state.data
        ) {
          const bulkData = query.state.data as Record<string, Category[]>;
          if (bulkData[eventId]) {
            cachedCategories = bulkData[eventId];
          }
        }
      });

      // If we found bulk cached data, return it
      if (cachedCategories.length > 0) {
        return cachedCategories;
      }

      // Otherwise, make individual API call
      assertParameterIsNotUndefinedOrNull(eventId, 'eventId');
      const categories = await sessionsApi
        .getCategoriesByEventSessionsEventsEventIdCategoriesGet({
          eventId,
        })
        .then(({ data }) => data.categories || []);

      return categories;
    },
    staleTime: 300000,
  });
};

// Hook for updating session category
export const useUpdateSessionCategory = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    SessionCategoriesOutput,
    unknown,
    { sessionId: string; categoryIds: string[]; communityId?: string }
  >({
    mutationFn: async ({
      sessionId,
      categoryIds,
      communityId,
    }: {
      sessionId: string;
      categoryIds: string[];
      communityId?: string;
    }) => {
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      const response =
        await sessionsApi.createSessionCategoriesSessionsSessionIdCommunitiesCommunityIdCategoriesPost(
          {
            sessionId,
            communityId: communityId as string,
            createSessionCategoriesInput: {
              session_categories: {
                session_id: sessionId,
                category_ids: categoryIds,
              },
            },
          },
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      const { sessionId, categoryIds } = variables;

      // DIRECT CACHE UPDATE: Set the session categories cache with the new category IDs
      // This is what useGetSessionCategories directly uses
      const sessionCategoriesData = categoryIds.map((categoryId) => ({
        session_id: sessionId,
        category_id: categoryId,
      }));

      // Set this data directly in the cache used by useGetSessionCategories
      queryClient.setQueryData(
        [sessionsKey, categoriesKey, sessionId],
        sessionCategoriesData,
      );
      console.log(
        `[useUpdateSessionCategory] Directly updated session categories cache for ${sessionId}`,
      );

      // CRITICAL: Instead of just invalidating the tagCategoriesDetailsKey cache,
      // we'll update it directly to force an immediate UI update
      // First, get the current cache data
      const currentTagsAndCatsData = queryClient.getQueryData<{
        tags: Tag[];
        categories: Category[];
      }>([sessionsKey, tagCategoriesDetailsKey, sessionId]);

      // Need to fetch the canonical category data for each category ID
      // This is critical as the SessionCardTags component needs this data immediately
      const fetchCategoryPromises = categoryIds.map(async (categoryId) => {
        // First check if we have it in cache
        const cachedCategory = queryClient.getQueryData<Category>([
          categoriesKey,
          categoryId,
        ]);
        if (cachedCategory) {
          return cachedCategory;
        }

        // If not in cache, fetch it
        try {
          const response =
            await sessionsApi.getCategorySessionsCategoriesCategoryIdGet({
              categoryId,
            });
          const categoryData = response.data.category;
          if (categoryData) {
            // Cache the canonical category data
            queryClient.setQueryData([categoriesKey, categoryId], categoryData);
            return categoryData;
          }
        } catch (error) {
          console.error(`Error fetching category ${categoryId}:`, error);
        }
        return null;
      });

      // Wait for all category data to be fetched
      Promise.all(fetchCategoryPromises).then((newCategories) => {
        // Filter out nulls
        const validCategories = newCategories.filter((cat): cat is Category =>
          Boolean(cat),
        );

        // Update the tags+categories cache directly
        queryClient.setQueryData(
          [sessionsKey, tagCategoriesDetailsKey, sessionId],
          {
            tags: currentTagsAndCatsData?.tags || [],
            categories: validCategories,
          },
        );

        console.log(
          `[useUpdateSessionCategory] Directly updated combined tags+categories cache with ${validCategories.length} categories`,
        );
      });

      // Update the bulk session data cache directly instead of invalidating
      const bulkQueries = queryClient.getQueriesData({
        queryKey: [sessionsKey, bulkSessionDataKey],
      });
      console.log(
        `[useUpdateSessionCategory] Found ${bulkQueries.length} bulk session data queries to update`,
      );

      let cacheUpdateCount = 0;

      // Update each bulk query cache that includes this session
      bulkQueries.forEach(([queryKey, queryData]) => {
        // Type guard to ensure queryData has the expected structure
        if (
          !queryData ||
          typeof queryData !== 'object' ||
          !('session_data' in queryData) ||
          !Array.isArray(queryData.session_data)
        ) {
          console.log(
            '[useUpdateSessionCategory] Skipping invalid bulk data format',
            queryKey,
          );
          return;
        }

        // Safe to use session_data now that we've confirmed it exists and is an array
        const typedData = queryData as {
          session_data: Array<{
            session_id: string;
            data: {
              session_categories?: Array<{
                session_id: string;
                category_id: string;
              }>;
            };
          }>;
        };

        // Find the session in the cache
        const sessionIndex = typedData.session_data.findIndex(
          (s) => s.session_id === sessionId,
        );
        if (sessionIndex === -1) {
          console.log(
            `[useUpdateSessionCategory] Session ${sessionId} not found in cache`,
            queryKey,
          );
          return;
        }

        // Update the data
        const updatedData = {
          ...typedData,
          session_data: [...typedData.session_data], // Create a new array to avoid mutating the cache
        };

        // Update the specific session
        updatedData.session_data[sessionIndex] = {
          ...updatedData.session_data[sessionIndex],
          data: {
            ...updatedData.session_data[sessionIndex].data,
            session_categories: sessionCategoriesData,
          },
        };

        // Update the cache with modified data
        queryClient.setQueryData(queryKey, updatedData);
        cacheUpdateCount++;
      });

      // Force refresh the SessionCardTags canonical category data
      const newCategoryPromises = categoryIds.map(async (categoryId) => {
        // Get cached canonical category data
        const existingData = queryClient.getQueryData([
          categoriesKey,
          categoryId,
        ]);
        if (!existingData) {
          console.log(
            `[useUpdateSessionCategory] Fetching canonical data for category ${categoryId}`,
          );
          // Force fetch the canonical data if not in cache
          try {
            const response =
              await sessionsApi.getCategorySessionsCategoriesCategoryIdGet({
                categoryId,
              });
            const categoryData = response.data.category;
            if (categoryData) {
              queryClient.setQueryData(
                [categoriesKey, categoryId],
                categoryData,
              );
            }
          } catch (error) {
            console.error(
              `[useUpdateSessionCategory] Error fetching category ${categoryId}:`,
              error,
            );
          }
        }
      });

      // Execute all promises in parallel
      void Promise.all(newCategoryPromises);

      console.log(
        `[useUpdateSessionCategory] Updated ${cacheUpdateCount} bulk data cache entries for session ${sessionId}`,
      );

      showSnackbar({
        text: 'Session category updated successfully!',
        type: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        text: 'Failed to update session category',
        type: 'error',
      });
    },
  });
};

// Hook for updating session tags
export const useUpdateSessionTags = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    SessionTagsOutput,
    unknown,
    { sessionId: string; tagIds: string[]; communityId?: string }
  >({
    mutationFn: async ({
      sessionId,
      communityId,
      tagIds,
    }: {
      sessionId: string;
      communityId?: string;
      tagIds: string[];
    }) => {
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');

      const result =
        await sessionsApi.updateSessionTagsSessionsSessionIdCommunitiesCommunityIdTagsPut(
          {
            sessionId,
            communityId: communityId as string,
            updateSessionTagsInput: {
              tag_ids: tagIds,
            },
          },
        );
      return result.data;
    },
    onSuccess: (_, variables) => {
      const { sessionId, tagIds } = variables;

      // DIRECT CACHE UPDATE: Set the session tags cache with the new tag IDs
      // This is what useGetSessionTags directly uses
      const sessionTagsData = tagIds.map((tagId) => ({
        session_id: sessionId,
        tag_id: tagId,
      }));

      // Set this data directly in the cache used by useGetSessionTags
      queryClient.setQueryData(
        [sessionsKey, tagsKey, sessionId],
        sessionTagsData,
      );
      console.log(
        `[useUpdateSessionTags] Directly updated session tags cache for ${sessionId}`,
      );

      // CRITICAL: Update the tagCategoriesDetailsKey cache directly for immediate UI update
      const currentTagsAndCatsData = queryClient.getQueryData<{
        tags: Tag[];
        categories: Category[];
      }>([sessionsKey, tagCategoriesDetailsKey, sessionId]);

      // Need to fetch the canonical tag data for each tag ID
      const fetchTagPromises = tagIds.map(async (tagId) => {
        // First check if we have it in cache
        const cachedTag = queryClient.getQueryData<Tag>([tagsKey, tagId]);
        if (cachedTag) {
          return cachedTag;
        }

        // If not in cache, fetch it
        try {
          const response = await sessionsApi.getTagSessionsTagsTagIdGet({
            tagId,
          });
          const tagData = response.data.tag;
          if (tagData) {
            // Cache the canonical tag data
            queryClient.setQueryData([tagsKey, tagId], tagData);
            return tagData;
          }
        } catch (error) {
          console.error(
            `[useUpdateSessionTags] Error fetching tag ${tagId}:`,
            error,
          );
        }
        return null;
      });

      // Wait for all tag data to be fetched
      Promise.all(fetchTagPromises).then((newTags) => {
        // Filter out nulls
        const validTags = newTags.filter((tag): tag is Tag => Boolean(tag));

        // Update the tags+categories cache directly
        queryClient.setQueryData(
          [sessionsKey, tagCategoriesDetailsKey, sessionId],
          {
            categories: currentTagsAndCatsData?.categories || [],
            tags: validTags,
          },
        );

        console.log(
          `[useUpdateSessionTags] Directly updated combined tags+categories cache with ${validTags.length} tags`,
        );
      });

      // Update the bulk session data cache directly
      const bulkQueries = queryClient.getQueriesData({
        queryKey: [sessionsKey, bulkSessionDataKey],
      });
      console.log(
        `[useUpdateSessionTags] Found ${bulkQueries.length} bulk session data queries to update`,
      );

      let cacheUpdateCount = 0;

      // Update each bulk query cache that includes this session
      bulkQueries.forEach(([queryKey, queryData]) => {
        // Type guard to ensure queryData has the expected structure
        if (
          !queryData ||
          typeof queryData !== 'object' ||
          !('session_data' in queryData) ||
          !Array.isArray(queryData.session_data)
        ) {
          console.log(
            '[useUpdateSessionTags] Skipping invalid bulk data format',
            queryKey,
          );
          return;
        }

        // Safe to use session_data now that we've confirmed it exists and is an array
        const typedData = queryData as {
          session_data: Array<{
            session_id: string;
            data: {
              session_tags?: Array<{ session_id: string; tag_id: string }>;
            };
          }>;
        };

        // Find the session in the cache
        const sessionIndex = typedData.session_data.findIndex(
          (s) => s.session_id === sessionId,
        );
        if (sessionIndex === -1) {
          console.log(
            `[useUpdateSessionTags] Session ${sessionId} not found in cache`,
            queryKey,
          );
          return;
        }

        // Update the data
        const updatedData = {
          ...typedData,
          session_data: [...typedData.session_data], // Create a new array to avoid mutating the cache
        };

        // Update the specific session
        updatedData.session_data[sessionIndex] = {
          ...updatedData.session_data[sessionIndex],
          data: {
            ...updatedData.session_data[sessionIndex].data,
            session_tags: sessionTagsData,
          },
        };

        // Update the cache with modified data
        queryClient.setQueryData(queryKey, updatedData);
        cacheUpdateCount++;
      });

      console.log(
        `[useUpdateSessionTags] Updated ${cacheUpdateCount} bulk data cache entries for session ${sessionId}`,
      );

      showSnackbar({
        text: 'Session tags updated successfully!',
        type: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        text: 'Failed to update session tags',
        type: 'error',
      });
    },
  });
};

export const useUpdateSession = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    onMutate: async ({
      sessionId,
      updateData,
    }: {
      sessionId: string;
      updateData: {
        topic_name?: string;
        topic_description?: string;
        user_id?: string;
        event_id?: string;
        status?: SessionStatus;
        start_date?: string;
        end_date?: string;
        location_id?: string | null;
      };
      gramRegenerationUserIds?: string[];
    }) => {
      // Pause outgoing refetches so we don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey: [sessionsKey] });

      const previousSession = queryClient.getQueryData<SessionOutput>([
        sessionsKey,
        sessionId,
      ]);
      const eventId = updateData.event_id || previousSession?.event_id;
      const eventSessionsKey = eventId
        ? ([sessionsKey, byEventIdKey, eventId] as const)
        : undefined;
      const previousEventSessions = eventSessionsKey
        ? queryClient.getQueryData<SessionOutput[]>(eventSessionsKey)
        : undefined;

      // Optimistically update status if provided
      if (updateData.status !== undefined) {
        if (previousSession) {
          queryClient.setQueryData<SessionOutput>([sessionsKey, sessionId], {
            ...previousSession,
            status: updateData.status,
          });
        }
        if (eventSessionsKey && previousEventSessions) {
          const updated = previousEventSessions.map((s) =>
            s.id === sessionId ? { ...s, status: updateData.status } : s,
          );
          queryClient.setQueryData(eventSessionsKey, updated);
        }
      }

      // Optionally optimistic title/description updates
      if (
        (updateData.topic_name || updateData.topic_description) &&
        previousSession
      ) {
        queryClient.setQueryData<SessionOutput>([sessionsKey, sessionId], {
          ...previousSession,
          ...(updateData.topic_name && { topic_name: updateData.topic_name }),
          ...(updateData.topic_description && {
            topic_description: updateData.topic_description,
          }),
        });
        if (eventSessionsKey && previousEventSessions) {
          const updated = previousEventSessions.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  ...(updateData.topic_name && {
                    topic_name: updateData.topic_name,
                  }),
                  ...(updateData.topic_description && {
                    topic_description: updateData.topic_description,
                  }),
                }
              : s,
          );
          queryClient.setQueryData(eventSessionsKey, updated);
        }
      }

      // Optimistically update start/end dates if provided
      if ((updateData.start_date || updateData.end_date) && previousSession) {
        queryClient.setQueryData<SessionOutput>([sessionsKey, sessionId], {
          ...previousSession,
          ...(updateData.start_date && { start_date: updateData.start_date }),
          ...(updateData.end_date && { end_date: updateData.end_date }),
        });
        if (eventSessionsKey && previousEventSessions) {
          const updated = previousEventSessions.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  ...(updateData.start_date && {
                    start_date: updateData.start_date,
                  }),
                  ...(updateData.end_date && { end_date: updateData.end_date }),
                }
              : s,
          );
          queryClient.setQueryData(eventSessionsKey, updated);
        }
      }

      // Optimistically update session_location_id if provided via location_id (string)
      if (typeof updateData.location_id === 'string') {
        const prev = queryClient.getQueryData<SessionOutput>([
          sessionsKey,
          sessionId,
        ]);
        const currentEventId = updateData.event_id || prev?.event_id;
        if (currentEventId) {
          const eventSessionsKey = [
            sessionsKey,
            byEventIdKey,
            currentEventId,
          ] as const;
          const prevList =
            queryClient.getQueryData<SessionOutput[]>(eventSessionsKey);
          if (prevList) {
            const updatedList = prevList.map((s) =>
              s.id === sessionId
                ? {
                    ...s,
                    location_id: updateData.location_id as string,
                  }
                : s,
            );
            queryClient.setQueryData(eventSessionsKey, updatedList);
          }
        }
        if (previousSession) {
          queryClient.setQueryData<SessionOutput>([sessionsKey, sessionId], {
            ...previousSession,
            location_id: updateData.location_id as string,
          });
        }
      }

      return {
        previousSession,
        previousEventSessions,
        eventId,
      } as {
        previousSession?: SessionOutput;
        previousEventSessions?: SessionOutput[];
        eventId?: string;
      };
    },
    mutationFn: async ({
      sessionId,
      updateData,
      communityId,
    }: {
      sessionId: string;
      updateData: {
        topic_name?: string;
        topic_description?: string;
        user_id?: string;
        event_id?: string;
        status?: SessionStatus;
        start_date?: string;
        end_date?: string;
        location_id?: string | null;
      };
      communityId?: string;
      gramRegenerationUserIds?: string[];
    }) => {
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');

      const { location_id, ...rest } = updateData;
      const updateSessionInput = {
        ...rest,
        ...(location_id !== undefined ? { location_id } : {}),
      } as unknown as UpdateSessionInput;

      const result =
        await sessionsApi.updateSessionSessionsSessionIdCommunitiesCommunityIdPut(
          {
            sessionId,
            communityId: communityId as string,
            updateSessionInput,
          },
        );

      // Store the updated session in the return value along with original API response
      return {
        apiResponse: result.data,
        previousSession: queryClient.getQueryData<SessionOutput>([
          sessionsKey,
          sessionId,
        ]),
        updatedSession: result.data, // API returns SessionOutput directly
      };
    },
    onSuccess: (data, { sessionId, updateData, gramRegenerationUserIds }) => {
      // Get the previous session, updated session, and the current event ID
      const { previousSession, updatedSession } = data;
      const eventId = updateData.event_id || previousSession?.event_id;

      // Invalidate lightweight individual session query
      void queryClient.invalidateQueries({
        queryKey: [sessionsKey, sessionId],
      });

      // Update bulk session data cache if key fields changed
      if (
        updateData.topic_name ||
        updateData.topic_description ||
        updateData.start_date ||
        updateData.end_date
      ) {
        // Get all bulk session data queries
        const bulkSessionQueries = queryClient.getQueriesData<{
          sessionData: Record<
            string,
            {
              topic_name?: string;
              topic_description?: string;
              start_date?: string;
              end_date?: string;
              status?: SessionStatus;
              event_id?: string;
            }
          >;
          sessionTags: Record<string, Array<{ tag_id: string }>>;
          sessionCategories: Record<string, Array<{ category_id: string }>>;
          sessionAttendees: Record<
            string,
            Array<{ id: string; role?: string }>
          >;
        }>({ queryKey: [sessionsKey, bulkSessionDataKey] });

        // Update each bulk session data query that might contain this session
        bulkSessionQueries.forEach(([queryKey, data]) => {
          if (data?.sessionData && data.sessionData[sessionId]) {
            // Create a new object to trigger React re-renders
            const updatedSessionData = {
              ...data,
              sessionData: {
                ...data.sessionData,
                [sessionId]: {
                  ...data.sessionData[sessionId],
                  // Update with new values from the mutation
                  ...(updateData.topic_name && {
                    topic_name: updateData.topic_name,
                  }),
                  ...(updateData.topic_description && {
                    topic_description: updateData.topic_description,
                  }),
                  ...(updateData.start_date && {
                    start_date: updateData.start_date,
                  }),
                  ...(updateData.end_date && {
                    end_date: updateData.end_date,
                  }),
                  // Include any other updated fields from the result
                  ...(updatedSession && {
                    status: updatedSession.status,
                    event_id: updatedSession.event_id,
                  }),
                },
              },
            };

            // Update the cache with the new session data
            queryClient.setQueryData(queryKey, updatedSessionData);
          }
        });

        // Also update sessions by event ID cache if available
        if (eventId) {
          const sessionsByEventQuery = queryClient.getQueryData<
            SessionOutput[]
          >([sessionsKey, byEventIdKey, eventId]);

          if (sessionsByEventQuery) {
            const updatedSessions = sessionsByEventQuery.map((session) => {
              if (session.id === sessionId) {
                return {
                  ...session,
                  ...(updateData.topic_name && {
                    topic_name: updateData.topic_name,
                  }),
                  ...(updateData.topic_description && {
                    topic_description: updateData.topic_description,
                  }),
                  ...(updateData.start_date && {
                    start_date: updateData.start_date,
                  }),
                  ...(updateData.end_date && { end_date: updateData.end_date }),
                  // Include any other updated fields from the updated session
                  ...(updatedSession && {
                    status: updatedSession.status,
                    event_id: updatedSession.event_id,
                  }),
                };
              }
              return session;
            });

            queryClient.setQueryData(
              [sessionsKey, byEventIdKey, eventId],
              updatedSessions,
            );
          }
        }
      }

      // Status update handling - always invalidate the sessions by event ID query
      // to ensure filtered views are updated immediately
      if (updateData.status !== undefined && eventId) {
        void queryClient.invalidateQueries({
          queryKey: [sessionsKey, byEventIdKey, eventId],
          // Only refetch immediately if we're already on the page that would display this
          refetchType: 'active',
        });
      }

      // After scheduling, trigger grams regeneration and social share creation for provided users
      if (
        updateData.status === SessionStatus.Scheduled &&
        gramRegenerationUserIds &&
        gramRegenerationUserIds.length > 0 &&
        (updatedSession?.event_id || previousSession?.event_id)
      ) {
        const effectiveEventId =
          updatedSession?.event_id ?? previousSession?.event_id;
        if (!effectiveEventId) {
          console.warn(
            'No event_id found for gram regeneration after scheduling',
          );
          return;
        }

        // Start gram regeneration for each user
        const GOOGLE_DRIVE_FOLDER_ID_FOR_SCHEDULE =
          '15Nlb7kHYFRkUXTyZqQTgWqjGf3ZMIMWM';
        Promise.allSettled(
          gramRegenerationUserIds.map((userId) =>
            gramsApi.createGramWithStorageGramsGeneratePost(
              {
                createGramWithStorageInput: {
                  user_id: userId,
                  event_id: effectiveEventId,
                  google_drive_folder_id: GOOGLE_DRIVE_FOLDER_ID_FOR_SCHEDULE,
                  file_format: 'PNG',
                },
              },
              { timeout: 300000 },
            ),
          ),
        )
          .then(() => {
            showSnackbar({
              text: 'Regenerating grams for speakers/moderators... Links will update shortly',
              type: 'info',
            });
          })
          .catch((e) => {
            console.error(
              'Error starting gram regeneration after scheduling:',
              e,
            );
          });

        // After 60 seconds, create new social shares with the refreshed gram storage
        setTimeout(() => {
          Promise.allSettled(
            gramRegenerationUserIds.map(async (userId) => {
              try {
                const { data: storage } =
                  await gramsApi.getGramStorageByUserAndEventGramsUsersUserIdEventsEventIdGet(
                    {
                      userId,
                      eventId: effectiveEventId,
                    },
                  );
                const gramStorageId = storage?.id;
                if (!gramStorageId) return;
                await gramsApi.createGramSocialShareGramsSocialSharesPost({
                  createGramSocialShareInput: {
                    gram_storage_id: gramStorageId,
                    event_id: effectiveEventId,
                    session_id: sessionId,
                  },
                });
                // Invalidate specific social share cache
                await queryClient.invalidateQueries({
                  queryKey: [socialSharesKey, sessionId, userId],
                });
              } catch (err) {
                console.error(
                  'Error creating social share after schedule gram regen:',
                  err,
                );
              }
            }),
          ).then(() => {
            showSnackbar({
              text: 'Share links regenerated for speakers/moderators',
              type: 'success',
            });
          });
        }, 60000);
      }

      // Invalidate sessions by event ID if event changed
      if (
        updateData.event_id &&
        updateData.event_id !== previousSession?.event_id
      ) {
        // Invalidate queries for both the old and new event ID
        if (previousSession?.event_id) {
          void queryClient.invalidateQueries({
            queryKey: [sessionsKey, byEventIdKey, previousSession.event_id],
          });
        }
        void queryClient.invalidateQueries({
          queryKey: [sessionsKey, byEventIdKey, updateData.event_id],
        });
      }
    },
    onError: (
      error,
      { sessionId },
      context:
        | {
            previousSession?: SessionOutput;
            previousEventSessions?: SessionOutput[];
            eventId?: string;
          }
        | undefined,
    ) => {
      console.error('Error updating session:', error);

      // Revert optimistic updates
      if (context?.previousSession) {
        queryClient.setQueryData(
          [sessionsKey, sessionId],
          context.previousSession,
        );
      }
      if (context?.eventId && context.previousEventSessions) {
        queryClient.setQueryData(
          [sessionsKey, byEventIdKey, context.eventId],
          context.previousEventSessions,
        );
      }

      showSnackbar({
        text: 'Failed to update session',
        type: 'error',
      });
    },
  });
};

export const useGetSessionsByTagsAndCategories = (
  input: GetSessionsByTagsOrCategoriesInput,
  eventId?: string,
) => {
  return useQuery({
    queryFn: async () => {
      return sessionsApi
        .getSessionsByTagsOrCategoriesSessionsSearchByTagsOrCategoriesPost({
          getSessionsByTagsOrCategoriesInput: input,
        })
        .then(({ data }) => data);
    },
    queryKey: [
      sessionsKey,
      byTagsKey,
      ...(input.category_ids || []),
      ...(input.tag_ids || []),
      eventId,
    ],
    enabled:
      Boolean(eventId) &&
      ((input.category_ids?.length || 0) > 0 ||
        (input.tag_ids?.length || 0) > 0),
  });
};

// Hook for fetching just the vote count for a session
export const useGetSessionVoteCount = (sessionId?: string) => {
  const { isQueryKeyAvailable } = useCacheAvailability();

  return useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(sessionId, 'sessionId');

      return sessionsApi
        .getSessionVoteCountsSessionsSessionIdVotesCountGet({
          sessionId,
        })
        .then((response) => response.data);
    },
    queryKey: [sessionsKey, votesKey, voteCountKey, sessionId],
    enabled:
      Boolean(sessionId) &&
      !isQueryKeyAvailable([sessionsKey, votesKey, voteCountKey]),
  });
};

// Hook for marking multiple sessions as viewed
export const useMarkSessionsAsViewed = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async (sessionIds: string[]) => {
      // Get currentUserId at execution time to avoid stale closure issues
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return sessionsApi
        .bulkMarkSessionsViewedSessionsUsersUserIdViewedPost({
          userId: currentUserId,
          bulkMarkSessionsViewedInput: {
            session_ids: sessionIds,
          },
        })
        .then(({ data }) => data);
    },
    async onSuccess(_, sessionIds) {
      // Update the cached data instead of invalidating queries

      // Update individual session queries
      for (const sessionId of sessionIds) {
        const sessionData = queryClient.getQueryData<SessionOutput>([
          sessionsKey,
          sessionId,
        ]);
        if (sessionData) {
          // Update the viewed status in the cached data
          queryClient.setQueryData([sessionsKey, sessionId], {
            ...sessionData,
            viewed: true,
          });
        }
      }

      // Update sessions by event ID queries
      const eventQueries = queryClient.getQueriesData<SessionOutput[]>({
        queryKey: [sessionsKey, byEventIdKey],
      });

      for (const [queryKey, sessionsData] of eventQueries) {
        if (sessionsData) {
          const updatedSessions = sessionsData.map((session) => {
            if (sessionIds.includes(session.id)) {
              return { ...session, viewed: true };
            }
            return session;
          });

          queryClient.setQueryData(queryKey, updatedSessions);
        }
      }

      // Update viewed sessions cache for all events
      // This is critical for the session card shadow color to update correctly
      const viewedQueries = queryClient.getQueriesData({
        queryKey: [sessionsKey, 'viewed'],
      });

      for (const [queryKey, viewedSessionsData] of viewedQueries) {
        if (viewedSessionsData && Array.isArray(viewedSessionsData)) {
          // Simply add the new session IDs to the existing array
          // First create a Set of existing IDs to avoid duplicates
          const existingIds = new Set(
            viewedSessionsData.map((session: { id: string }) => session.id),
          );

          // Create new session objects for IDs that don't exist in the cache yet
          const newSessions = sessionIds
            .filter((id) => !existingIds.has(id))
            .map((id) => ({ id }));

          // Only update if we have new sessions to add
          if (newSessions.length > 0) {
            queryClient.setQueryData(queryKey, [
              ...viewedSessionsData,
              ...newSessions,
            ]);
          }
        }
      }
    },
    onError: (error) => {
      console.error('Error marking sessions as viewed:', error);
      showSnackbar({
        text: 'Failed to mark sessions as viewed',
        type: 'error',
      });
    },
  });
};

// Hook for marking multiple sessions as not viewed
export const useMarkSessionsAsNotViewed = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async (sessionIds: string[]) => {
      // Get currentUserId at execution time to avoid stale closure issues
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return sessionsApi
        .bulkMarkSessionsNotViewedSessionsUsersUserIdViewedDelete({
          userId: currentUserId,
          bulkMarkSessionsNotViewedInput: {
            session_ids: sessionIds,
          },
        })
        .then(({ data }) => data);
    },
    async onSuccess(_, sessionIds) {
      // Get currentUserId at execution time for cache operations
      const { currentUserId } = useAuthStore.getState();

      // Update the individual user viewed sessions query
      const userViewedSessionsQueryKey = [
        sessionsKey,
        viewedKey,
        currentUserId,
      ];
      const currentViewedSessions = queryClient.getQueryData<
        Array<{ id: string }>
      >(userViewedSessionsQueryKey);

      if (currentViewedSessions) {
        const updatedViewedSessions = currentViewedSessions.filter(
          (viewedSession) => !sessionIds.includes(viewedSession.id),
        );
        queryClient.setQueryData(
          userViewedSessionsQueryKey,
          updatedViewedSessions,
        );
      }

      // Update any sessions by event ID queries to mark these sessions as not viewed
      const eventQueries = queryClient.getQueriesData<SessionOutput[]>({
        queryKey: [sessionsKey, byEventIdKey],
      });

      for (const [queryKey, sessionsData] of eventQueries) {
        if (sessionsData) {
          const updatedSessions = sessionsData.map((session) => {
            if (sessionIds.includes(session.id)) {
              return { ...session, viewed: false };
            }
            return session;
          });

          queryClient.setQueryData(queryKey, updatedSessions);
        }
      }
    },
    onError: (error) => {
      console.error('Error marking sessions as not viewed:', error);
      showSnackbar({
        text: 'Failed to mark sessions as not viewed',
        type: 'error',
      });
    },
  });
};

// Hook for deleting a tag
export const useDeleteTag = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async ({
      tagId,
      communityId,
    }: {
      tagId: string;
      communityId?: string;
    }) => {
      assertParameterIsNotUndefinedOrNull(tagId, 'tagId');
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      return sessionsApi.deleteTagSessionsTagsTagIdCommunitiesCommunityIdDelete(
        {
          tagId,
          communityId: communityId as string,
        },
      );
    },
    onSuccess: (_, { tagId }) => {
      // Invalidate tags queries to refresh the list
      void queryClient.invalidateQueries({
        queryKey: [sessionsKey, tagsKey],
      });

      // Remove the individual tag from cache
      queryClient.removeQueries({
        queryKey: [tagsKey, tagId],
      });

      showSnackbar({
        text: 'Tag deleted successfully!',
        type: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        text: 'Failed to delete tag',
        type: 'error',
      });
    },
  });
};

// Hook for deleting a category
export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async ({
      categoryId,
      communityId,
    }: {
      categoryId: string;
      communityId?: string;
    }) => {
      assertParameterIsNotUndefinedOrNull(categoryId, 'categoryId');
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      return sessionsApi.deleteCategorySessionsCategoriesCategoryIdCommunitiesCommunityIdDelete(
        {
          categoryId,
          communityId: communityId as string,
        },
      );
    },
    onSuccess: (_, { categoryId }) => {
      // Invalidate categories queries to refresh the list
      void queryClient.invalidateQueries({
        queryKey: [sessionsKey, categoriesKey],
      });

      // Remove the individual category from cache
      queryClient.removeQueries({
        queryKey: [categoriesKey, categoryId],
      });

      showSnackbar({
        text: 'Category deleted successfully!',
        type: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        text: 'Failed to delete category',
        type: 'error',
      });
    },
  });
};

// Hook for joining a session as an attendee with a specific role
export const useJoinSession = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async ({
      sessionId,
      joinSessionInput,
    }: {
      sessionId: string;
      joinSessionInput: JoinSessionInput;
      eventId?: string;
    }) => {
      const response = await sessionsApi.joinSessionSessionsSessionIdJoinPost({
        sessionId,
        joinSessionInput,
      });
      return response.data;
    },
    onSuccess: (_, { sessionId, eventId }) => {
      // Invalidate session attendees to refresh the list
      void queryClient.invalidateQueries({
        queryKey: [sessionsKey, attendeesKey, sessionId],
      });

      // Invalidate bulk session data that might contain attendee information
      void queryClient.invalidateQueries({
        queryKey: [sessionsKey, bulkSessionDataKey],
      });

      // Also refresh event sessions list/cards if eventId provided
      if (eventId) {
        void queryClient.invalidateQueries({
          queryKey: [sessionsKey, byEventIdKey, eventId],
        });
      }
    },
    onError: (error) => {
      console.error('Error joining session:', error);
      showSnackbar({
        text: 'Failed to add attendee to session',
        type: 'error',
      });
    },
  });
};

// Hook for fetching session vote analytics data
export const useGetSessionVoteAnalytics = (
  eventId?: string,
  startDate?: string,
  endDate?: string,
  options?: { enabled?: boolean; communityId?: string },
) => {
  return useQuery({
    queryKey: [analyticsKey, eventId, startDate, endDate, options?.communityId],
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(eventId, 'eventId');
      assertParameterIsNotUndefinedOrNull(startDate, 'startDate');
      assertParameterIsNotUndefinedOrNull(endDate, 'endDate');
      assertParameterIsNotUndefinedOrNull(options?.communityId, 'communityId');

      const response =
        await sessionsApi.getSessionVoteAnalyticsSessionsVotesAnalyticsCommunitiesCommunityIdPost(
          {
            communityId: options?.communityId as string,
            sessionVoteAnalyticsInput: {
              event_id: eventId,
              start_date: startDate,
              end_date: endDate,
            },
          },
        );
      return response.data;
    },
    enabled:
      Boolean(eventId && startDate && endDate) && (options?.enabled ?? true),
  });
};

// Fetch all session locations for a given event
export const useGetSessionLocationsByEvent = (eventId?: string) => {
  return useQuery({
    queryKey: [sessionsKey, locationsKey, byEventIdKey, eventId],
    enabled: Boolean(eventId),
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(eventId, 'eventId');
      const response =
        await sessionsApi.getSessionLocationsByEventSessionsLocationsEventsEventIdGet(
          { eventId: eventId as string },
        );
      return response.data;
    },
    staleTime: 300000,
  });
};

// Bulk Session Locations: Get session locations for multiple event IDs
export const useBulkGetSessionLocations = (eventIds?: string[]) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [sessionsKey, locationsKey, 'bulk', eventIds],
    enabled: Boolean(eventIds?.length),
    queryFn: async (): Promise<BulkGetSessionLocationsOutput> => {
      assertParameterIsNotUndefinedOrNull(eventIds, 'eventIds');

      const data = await sessionsApi
        .bulkGetSessionLocationsSessionsLocationsBulkPost({
          bulkGetSessionLocationsInput: {
            event_ids: eventIds,
          },
        })
        .then((response) => response.data);

      // Transform flat array to event_id -> locations mapping
      const sessionLocations = data?.session_locations || [];

      for (const location of sessionLocations) {
        queryClient.setQueryData(
          [sessionsKey, locationsKey, byEventIdKey, location.event_id],
          location,
        );
        queryClient.setQueryData(
          [sessionsKey, locationsKey, location.id],
          location,
        );
      }

      return data;
    },
    staleTime: 300000,
  });
};

export const useGetSessionLocation = (sessionLocationId?: string) => {
  const { isQueryKeyAvailable } = useCacheAvailability();
  return useQuery({
    queryKey: [sessionsKey, locationsKey, sessionLocationId],
    enabled:
      Boolean(sessionLocationId) &&
      !isQueryKeyAvailable([sessionsKey, locationsKey]),
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(
        sessionLocationId,
        'sessionLocationId',
      );

      return sessionsApi
        .getSessionLocationSessionsLocationsSessionLocationIdGet({
          sessionLocationId,
        })
        .then((response) => response.data);
    },
  });
};

// Cache-aware Session Locations: Use bulk cache if available, otherwise fall back to individual call
export const useGetSessionLocationsByEventWithBulkFallback = (
  eventId?: string,
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [sessionsKey, locationsKey, byEventIdKey, eventId],
    enabled: Boolean(eventId),
    queryFn: async (): Promise<SessionLocation[]> => {
      if (!eventId) return [];

      // First, try to find the data in bulk caches
      const bulkCaches = queryClient.getQueriesData({
        queryKey: [sessionsKey, locationsKey, 'bulk'],
      });

      for (const [, cacheData] of bulkCaches) {
        if (cacheData && typeof cacheData === 'object') {
          const bulkData = cacheData as Record<string, SessionLocation[]>;
          if (bulkData[eventId]) {
            return bulkData[eventId];
          }
        }
      }

      // Fallback to individual API call if not found in bulk cache
      assertParameterIsNotUndefinedOrNull(eventId, 'eventId');
      const response =
        await sessionsApi.getSessionLocationsByEventSessionsLocationsEventsEventIdGet(
          { eventId },
        );
      return response.data || [];
    },
    staleTime: 300000,
  });
};

// Create a new session location under an event
export const useCreateSessionLocationForEvent = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    SessionLocation,
    unknown,
    {
      eventId: string;
      createSessionLocationInput: Omit<CreateSessionLocationInput, 'event_id'>;
    }
  >({
    async mutationFn({ eventId, createSessionLocationInput }) {
      // Ensure body includes the correct event_id per API contract
      const body: CreateSessionLocationInput = {
        event_id: eventId,
        name: createSessionLocationInput.name,
        address: createSessionLocationInput.address,
        city: createSessionLocationInput.city,
        state: createSessionLocationInput.state,
        postal_code: createSessionLocationInput.postal_code,
      };

      const response =
        await sessionsApi.createSessionLocationSessionsLocationsEventsEventIdPost(
          {
            eventId,
            createSessionLocationInput: body,
          },
        );
      return response.data;
    },
    async onSuccess(_data, { eventId }) {
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, locationsKey, byEventIdKey, eventId],
      });
      showSnackbar({ text: 'Location created', type: 'success' });
    },
    async onError() {
      showSnackbar({ text: 'Failed to create location', type: 'error' });
    },
  });
};

export const useUpdateSessionLocation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    SessionLocation,
    unknown,
    {
      sessionLocationId: string;
      updateSessionLocationInput: UpdateSessionLocationInput;
      eventId: string;
    }
  >({
    async mutationFn({ sessionLocationId, updateSessionLocationInput }) {
      const response =
        await sessionsApi.updateSessionLocationSessionsLocationsSessionLocationIdPut(
          {
            sessionLocationId,
            updateSessionLocationInput,
          },
        );
      return response.data;
    },
    async onSuccess(_data, { eventId }) {
      // Invalidate session locations list for the event
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, locationsKey, byEventIdKey, eventId],
      });
      // Invalidate specific location
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, locationsKey, _data.id],
      });
      showSnackbar({ text: 'Session location updated', type: 'success' });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to update session location',
        type: 'error',
      });
    },
  });
};

export const useDeleteSessionLocation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    DeleteSessionLocationOutput,
    unknown,
    { sessionLocationId: string; eventId: string }
  >({
    async mutationFn({ sessionLocationId }) {
      const response =
        await sessionsApi.deleteSessionLocationSessionsLocationsSessionLocationIdDelete(
          {
            sessionLocationId,
          },
        );
      return response.data;
    },
    async onSuccess(_data, { eventId }) {
      // Invalidate session locations list for the event
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, locationsKey, byEventIdKey, eventId],
      });
      showSnackbar({ text: 'Session location removed', type: 'success' });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to remove session location',
        type: 'error',
      });
    },
  });
};

// Confirm a session via dedicated endpoint: PATCH /sessions/{session_id}/confirm
export const useConfirmSession = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    onMutate: async ({ sessionId }: { sessionId: string }) => {
      // Pause outgoing refetches
      await queryClient.cancelQueries({ queryKey: [sessionsKey] });

      // Try to get the individual session and event list caches
      const previousSession = queryClient.getQueryData<SessionOutput>([
        sessionsKey,
        sessionId,
      ]);

      let eventId: string | undefined = previousSession?.event_id;
      let previousEventSessions: SessionOutput[] | undefined;

      if (eventId) {
        previousEventSessions = queryClient.getQueryData<SessionOutput[]>([
          sessionsKey,
          byEventIdKey,
          eventId,
        ]);
      } else {
        // Search through sessions-by-event caches to find which event contains this session
        const eventQueries = queryClient.getQueriesData<SessionOutput[]>({
          queryKey: [sessionsKey, byEventIdKey],
        });
        for (const [, data] of eventQueries) {
          const found = data?.find((s) => s.id === sessionId);
          if (found) {
            eventId = found.event_id;
            previousEventSessions = data;
            // Ensure we have the exact key for setQueryData below
            break;
          }
        }
      }

      // Optimistically set status to Confirmed
      if (previousSession) {
        queryClient.setQueryData<SessionOutput>([sessionsKey, sessionId], {
          ...previousSession,
          status: SessionStatus.Confirmed,
        });
      }
      if (eventId && previousEventSessions) {
        const updated = previousEventSessions.map((s) =>
          s.id === sessionId ? { ...s, status: SessionStatus.Confirmed } : s,
        );
        queryClient.setQueryData([sessionsKey, byEventIdKey, eventId], updated);
      }

      return {
        previousSession,
        previousEventSessions,
        eventId,
      } as {
        previousSession?: SessionOutput;
        previousEventSessions?: SessionOutput[];
        eventId?: string;
      };
    },
    mutationFn: async ({ sessionId }: { sessionId: string }) => {
      const result =
        await sessionsApi.confirmSessionSessionsSessionIdConfirmPatch({
          sessionId,
        });
      return result.data;
    },
    onSuccess: (_data, { sessionId }, context) => {
      // Invalidate lightweight queries to refresh server truth
      void queryClient.invalidateQueries({
        queryKey: [sessionsKey, sessionId],
      });
      if (context?.eventId) {
        void queryClient.invalidateQueries({
          queryKey: [sessionsKey, byEventIdKey, context.eventId],
          refetchType: 'active',
        });
      }

      showSnackbar({ text: 'Session confirmed!', type: 'success' });
    },
    onError: (error, { sessionId }, context) => {
      console.error('Error confirming session:', error);
      // Revert optimistic updates
      if (context?.previousSession) {
        queryClient.setQueryData(
          [sessionsKey, sessionId],
          context.previousSession,
        );
      }
      if (context?.eventId && context?.previousEventSessions) {
        queryClient.setQueryData(
          [sessionsKey, byEventIdKey, context.eventId],
          context.previousEventSessions,
        );
      }
      showSnackbar({ text: 'Failed to confirm session', type: 'error' });
    },
  });
};

// Session Favorite Hooks
export const useGetSessionFavoriteCount = (
  sessionId?: string,
  communityId?: string,
) => {
  const queryClient = useQueryClient();
  const { isQueryKeyAvailable } = useCacheAvailability();

  // Check if cache data actually exists for this session
  const cacheKey = [sessionsKey, favoritesKey, favoriteCountKey, sessionId];

  return useQuery({
    queryFn: async () => {
      // First check if bulk cache has the data (without communityId in key)
      const bulkCachedCount = queryClient.getQueryData<number>(cacheKey);

      if (bulkCachedCount !== undefined) {
        return bulkCachedCount;
      }

      // Fall back to API call if not in bulk cache (requires communityId)
      assertParameterIsNotUndefinedOrNull(sessionId, 'sessionId');
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      return sessionsApi
        .getSessionFavoritesSessionsSessionIdCommunitiesCommunityIdFavoritesGet(
          {
            sessionId,
            communityId,
          },
        )
        .then((response) => response.data.favorites?.length ?? 0);
    },
    // Note: queryKey only includes sessionId (not communityId) to match bulk cache
    // Favorite counts are per-session, communityId is only for authorization
    queryKey: [sessionsKey, favoritesKey, favoriteCountKey, sessionId],
    enabled:
      Boolean(sessionId && communityId) &&
      !isQueryKeyAvailable([sessionsKey, favoritesKey, favoriteCountKey]),
  });
};

export const useGetSessionFavorites = (
  sessionId?: string,
  communityId?: string,
) => {
  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(sessionId, 'sessionId');
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      return sessionsApi
        .getSessionFavoritesSessionsSessionIdCommunitiesCommunityIdFavoritesGet(
          {
            sessionId,
            communityId,
          },
        )
        .then((response) => response.data.favorites ?? []);
    },
    queryKey: [sessionsKey, favoritesKey, sessionId, communityId],
    enabled: Boolean(sessionId && communityId),
  });
};

/**
 * @deprecated Use useGetBulkSessionFavoriteCountsV2 instead
 */
export const useGetBulkSessionFavoriteCounts = ({
  sessionIds,
  eventId,
  enabled = true,
}: {
  sessionIds: string[];
  eventId: string;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [sessionsKey, bulkFavoritesKey, eventId],
    queryFn: async () => {
      if (sessionIds.length === 0) {
        return { counts: [] as SessionFavoriteCountOutput[] };
      }

      const response = await sessionsApi
        .bulkGetSessionFavoriteCountsSessionsFavoritesCountBulkPost({
          bulkSessionIdsInput: {
            session_ids: sessionIds,
          },
        })
        .then((response) => response.data);

      // Cache individual favorite counts
      if (response.counts && Array.isArray(response.counts)) {
        response.counts.forEach((favoriteCount) => {
          if (favoriteCount.session_id) {
            queryClient.setQueryData(
              [
                sessionsKey,
                favoritesKey,
                favoriteCountKey,
                favoriteCount.session_id,
              ],
              favoriteCount.favorite_count ?? 0,
            );
          }
        });
      }

      return response;
    },
    enabled: enabled && sessionIds.length > 0,
  });
};

export const useGetBulkSessionFavoriteCountsV2 = ({
  sessionIds,
  enabled = true,
}: {
  sessionIds: string[];
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [sessionsKey, bulkFavoritesKey, sessionIds],
    queryFn: async () => {
      if (sessionIds.length === 0) {
        return { counts: [] as SessionFavoriteCountOutput[] };
      }

      const response = await sessionsApi
        .bulkGetSessionFavoriteCountsSessionsFavoritesCountBulkPost({
          bulkSessionIdsInput: {
            session_ids: sessionIds,
          },
        })
        .then((response) => response.data);

      // Cache individual favorite counts
      if (response.counts && Array.isArray(response.counts)) {
        response.counts.forEach((favoriteCount) => {
          if (favoriteCount.session_id) {
            queryClient.setQueryData(
              [
                sessionsKey,
                favoritesKey,
                favoriteCountKey,
                favoriteCount.session_id,
              ],
              favoriteCount.favorite_count ?? 0,
            );
          }
        });
      }

      return response;
    },
    enabled: enabled && sessionIds.length > 0,
  });
};

export const useGetUserSessionFavoriteIds = ({
  eventId,
  communityId,
  enabled,
}: {
  eventId?: string;
  communityId?: string;
  enabled?: boolean;
}) => {
  const { currentUserId } = useAuthStore();
  const { isQueryKeyAvailable } = useCacheAvailability();

  return useAuthQuery({
    queryFn: async (): Promise<string[]> => {
      assertParameterIsNotUndefinedOrNull(eventId, 'eventId');
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return sessionsApi
        .getUserFavoritedSessionsSessionsCommunitiesCommunityIdEventsEventIdUsersUserIdFavoritesGet(
          {
            eventId: eventId,
            userId: currentUserId,
            communityId: communityId,
          },
        )
        .then(({ data }) => data.sessions.map((session) => session.id));
    },
    queryKey: [sessionsKey, userSessionFavoritesKey, eventId],
    enabled:
      Boolean(enabled && eventId && communityId) &&
      !isQueryKeyAvailable([sessionsKey, userSessionFavoritesKey]),
  });
};

export const useGetUserFavoriteSessions = ({
  userId,
  temporalRelation = TemporalRelation.Future,
  enabled = true,
}: {
  userId?: string;
  temporalRelation?: TemporalRelation;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useAuthQuery({
    queryKey: [
      sessionsKey,
      allUserFavoritesKey,
      temporalKey,
      temporalRelation,
      userId,
    ],
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(userId, 'userId');

      const response =
        await sessionsApi.getUserFavoritedSessionsTemporalSessionsUsersUserIdFavoritesTemporalGet(
          {
            userId,
            temporalRelation,
          },
        );

      const favoritedSessions = response.data.sessions || [];

      // Cache each session individually
      const eventIdToSessionIds = new Map<string, string[]>();
      for (const session of favoritedSessions) {
        queryClient.setQueryData([sessionsKey, session.id], session);
        const currentSessionIds =
          eventIdToSessionIds.get(session.event_id) || [];
        eventIdToSessionIds.set(session.event_id, [
          ...currentSessionIds,
          session.id,
        ]);
      }

      for (const [eventId, sessionIds] of eventIdToSessionIds) {
        queryClient.setQueryData(
          [sessionsKey, userSessionFavoritesKey, eventId],
          sessionIds,
        );
      }

      return favoritedSessions;
    },
    enabled: Boolean(userId) && enabled,
  });
};

export const useFavoriteSession = (sessionId: string, eventId?: string) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async () => {
      if (!sessionId) return;
      // Get currentUserId at execution time to avoid stale closure issues
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return sessionsApi
        .favoriteSessionSessionsSessionIdFavoritePost({
          sessionFavoriteInput: {
            user_id: currentUserId,
          },
          sessionId,
        })
        .then((response) => response.data);
    },
    async onSuccess() {
      queryClient.setQueryData<number>(
        [sessionsKey, favoritesKey, favoriteCountKey, sessionId],
        (oldCount) => (oldCount ?? 0) + 1,
      );

      queryClient.setQueryData<string[]>(
        [sessionsKey, userSessionFavoritesKey, eventId],
        (oldSessions) => [...(oldSessions ?? []), sessionId],
      );
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, allUserFavoritesKey],
      });
    },
    async onError(error) {
      queryClient.setQueryData<number>(
        [sessionsKey, favoritesKey, favoriteCountKey, sessionId],
        (oldCount) => (oldCount ?? 0) - 1,
      );

      queryClient.setQueryData<string[]>(
        [sessionsKey, userSessionFavoritesKey, eventId],
        (oldSessions) =>
          oldSessions?.filter((session) => session !== sessionId),
      );

      // re-fetching
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, favoritesKey, favoriteCountKey, sessionId],
      });
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, userSessionFavoritesKey, eventId],
      });
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, allUserFavoritesKey],
      });
      if (isAxiosError(error) && error.response?.status === 400) {
        showSnackbar({
          type: 'error',
          text: "You've already favorited this session!",
        });
      } else {
        showSnackbar({
          type: 'error',
          text: 'Failed to favorite session. Please try again',
        });
      }
    },
  });
};

export const useRemoveFavoriteSession = (
  sessionId: string,
  eventId?: string,
) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    mutationFn: async () => {
      if (!sessionId) return;
      // Get currentUserId at execution time to avoid stale closure issues
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return sessionsApi
        .removeSessionFavoriteSessionsSessionIdFavoriteUserIdDelete({
          userId: currentUserId,
          sessionId,
        })
        .then((response) => response.data);
    },
    async onSuccess() {
      queryClient.setQueryData<number>(
        [sessionsKey, favoritesKey, favoriteCountKey, sessionId],
        (oldCount) => (oldCount ?? 0) - 1,
      );

      queryClient.setQueryData<string[]>(
        [sessionsKey, userSessionFavoritesKey, eventId],
        (oldSessions) =>
          oldSessions?.filter((session) => session !== sessionId),
      );

      // Invalidate all user favorites queries (for the new hook)
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, allUserFavoritesKey],
      });
    },
    async onError() {
      queryClient.setQueryData<number>(
        [sessionsKey, favoritesKey, favoriteCountKey, sessionId],
        (oldCount) => (oldCount ?? 0) + 1,
      );

      queryClient.setQueryData<string[]>(
        [sessionsKey, userSessionFavoritesKey, eventId],
        (oldSessions) => [...(oldSessions ?? []), sessionId],
      );
      // re-fetching
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, favoritesKey, favoriteCountKey, sessionId],
      });
      // Invalidate user session favorites
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, userSessionFavoritesKey, eventId],
      });

      // Invalidate all user favorites queries (for the new hook)
      await queryClient.invalidateQueries({
        queryKey: [sessionsKey, allUserFavoritesKey],
      });
      showSnackbar({
        type: 'error',
        text: 'Failed to remove favorite. Please try again',
      });
    },
  });
};
